import { z } from 'zod';
import { supabase } from '@/utils/supabase';

const resultSchema = z.object({
  success: z.literal(true),
  enabled: z.boolean().optional(),
  alreadySent: z.boolean().optional(),
  recipientEmail: z.string().email(),
  ccEmails: z.array(z.string().email()).default([]),
  recipientName: z.string().optional(),
  emailId: z.string().optional(),
  auditLogged: z.boolean().optional()
});
export type BonusEmailResult = z.infer<typeof resultSchema>;

export class BonusEmailError extends Error {
  readonly retryable: boolean;

  constructor(message: string, readonly status?: number, readonly code?: string, retryable?: boolean) {
    super(message);
    this.name = 'BonusEmailError';
    this.retryable = retryable !== false && (status === undefined || status >= 500 || status === 408 || status === 429);
  }
}

/** Saving a bonus is a different operation. This service never calls a money RPC. */
export async function requestBonusEmail(bonusId: string, expectedVersion: number, action: 'preview' | 'send'): Promise<BonusEmailResult> {
  const { data, error } = await supabase.functions.invoke('send-notification', {
    body: { notificationType: 'lo_profitability_bonus_awarded', data: { bonusId, expectedVersion, action } }
  }).catch(() => {
    throw new BonusEmailError('Email delivery could not be confirmed. Your bonus remains saved.');
  });
  if (error) {
    let message = 'Email delivery could not be confirmed. Your bonus remains saved.';
    const context = (error as { context?: Response }).context;
    let code: string | undefined;
    let retryable: boolean | undefined;
    if (context && typeof context.json === 'function') {
      const body = await context.json().catch(() => null) as { error?: unknown; code?: unknown; retryable?: unknown } | null;
      if (typeof body?.error === 'string') {
        message = body.error;
      }
      code = typeof body?.code === 'string' ? body.code : undefined;
      retryable = typeof body?.retryable === 'boolean' ? body.retryable : undefined;
    }
    throw new BonusEmailError(message, context?.status, code, retryable);
  }
  const parsed = resultSchema.safeParse(data);
  if (!parsed.success) {
    throw new BonusEmailError('Email status could not be confirmed. Your bonus remains saved.');
  }
  return parsed.data;
}
